import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAccount, useUpdateAccount } from 'hooks/api/useAccount';
import { useApplications } from 'hooks/api/useApplications';
import { useAuthentication } from 'hooks/api/useAuthentication';
import { useAPI } from 'hooks/api/useAPI';
import { useRegistrationParams } from 'hooks/useRegistrationParams';

import { setupRingAccount } from 'services/account_setup';

import LogoIcon from 'images/lumeo-logo-icon.svg?react';

import { Button } from 'components/Button/Button';
import { Heading } from 'components/Heading/Heading';
import { Loader } from 'components/Loader/Loader';
import { Text } from 'components/Text/Text';
import { FormMessage } from 'components/FormMessage/FormMessage';

export function ConfirmRing() {
  const api = useAPI();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthentication();
  const { data: account, isLoading, refetch } = useAccount();
  const { mutate: updateAccount } = useUpdateAccount();
  const { data: applications, isLoading: isLoadingApplications } =
    useApplications();
  const { getParams, clearParams } = useRegistrationParams();
  const registrationParams = getParams();

  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);

  // Determine ring link state from account attributes or session params
  const ringLinkState =
    account?.account_attributes?.ring_link_state ||
    (registrationParams?.partner === 'ring' ? 'unlinked' : undefined);

  async function handleConfirmClick() {
    if (!applications?.length) {
      console.error('No application found');
      return;
    }

    if (!account) {
      console.error('No account found');
      return;
    }

    const application = applications[0];
    setIsConfirming(true);

    // Create Ring dashboard and set it as default
    try {
      await setupRingAccount(api, account, application.id);
    } catch (error) {
      console.error('Failed to create Ring dashboard:', error);
      // Continue with redirect even if dashboard creation fails
    }

    // Confirm partner integration
    try {
      await api.applications.partnerConfirm(application.id);
      const { data: updatedAccount } = await refetch();

      const lumeoCode = updatedAccount?.account_attributes?.lumeo_code;
      const redirectUri = updatedAccount?.account_attributes?.redirect_uri;
      const state = updatedAccount?.account_attributes?.state;

      if (lumeoCode && redirectUri && state) {
        // Redirect to Ring with the code
        window.location.href = `${redirectUri}?state=${encodeURIComponent(state)}&code=${encodeURIComponent(lumeoCode)}`;
      } else {
        console.error('Missing required attributes for redirect');
        setIsConfirming(false);
      }
    } catch (error) {
      console.error('Failed to confirm partner integration:', error);
      setIsConfirming(false);
    }
  }

  function handleProceed() {
    clearParams();
    navigate('/');
  }

  async function handleResetLinking() {
    if (!account?.account_attributes) {
      return;
    }

    setIsResetting(true);

    try {
      // Reset ring_link_state to unlinked in account attributes
      // The account attributes already have the updated redirect_uri and state
      // from useSyncRingAccountAttributes, so we just need to reset the link state
      const updatedAttrs = {
        ...account.account_attributes,
        ring_link_state: 'unlinked' as const,
      };

      // Update account attributes
      await new Promise<void>((resolve) => {
        updateAccount(
          {
            account_attributes: updatedAttrs,
          } as any,
          {
            onSuccess: () => resolve(),
          }
        );
      });

      // Refetch to get updated account data and enter confirmation flow
      await refetch();

      setIsResetting(false);
    } catch (error) {
      console.error('Failed to reset linking:', error);
      setIsResetting(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  if (isLoading || isLoadingApplications) {
    return <Loader text="Loading..." />;
  }

  // If linked and has registration params, show already-linked message
  const isAlreadyLinkedWithParams =
    ringLinkState === 'linked' && registrationParams?.partner === 'ring';

  // If linked without registration params, redirect to home
  if (ringLinkState === 'linked' && !isAlreadyLinkedWithParams) {
    return <Navigate to="/" />;
  }

  // If not a ring partner, redirect to home
  if (!ringLinkState) {
    return <Navigate to="/" />;
  }

  return (
    <main className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md space-y-8 px-4">
        <header className="flex flex-col items-center space-y-6">
          <LogoIcon className="h-12 w-auto" />

          <Heading level="1" className="text-center">
            {isAlreadyLinkedWithParams
              ? 'Account Already Linked'
              : ringLinkState === 'unlinked'
                ? 'Confirm to link with Ring'
                : 'Please confirm the Integration in the Ring App'}
          </Heading>

          {isAlreadyLinkedWithParams && (
            <>
              <Text size="large" className="text-center text-gray-600">
                Your Lumeo account was previously linked to Ring.
              </Text>

              <div className="mt-8 space-y-4 rounded-lg border border-gray-400 p-6">
                <Text size="large" className="text-gray-600">
                  If you wish to relink your Lumeo account with new Ring
                  authorization, click "Relink Account" below.
                </Text>
                <Text size="large" className="pt-4 text-gray-600">
                  Alternatively, you can keep it linked with your old Ring
                  account and proceed to Lumeo dashboard.
                </Text>
              </div>

              <div className="flex w-full flex-col gap-4 pt-4 sm:flex-row sm:justify-center">
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleResetLinking}
                  loading={isResetting}
                >
                  Relink with new Ring account
                </Button>
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleProceed}
                >
                  Keep old Ring account linked
                </Button>
              </div>
            </>
          )}

          {ringLinkState === 'unlinked' && (
            <>
              <Text size="large" className="text-center text-gray-600">
                Click the button below to complete the integration with your
                Ring account.
              </Text>

              <div className="mt-8 space-y-4 rounded-lg border border-gray-400 p-6">
                <Heading
                  level="3"
                  className="pt-4 text-sm font-semibold text-gray-900"
                >
                  How it works
                </Heading>
                <Text size="large" className="text-gray-600">
                  Lumeo will process Motion events from your Ring cameras and
                  run the configured AI workflows on those recordings to
                  generate alerts and dashboards.
                </Text>
                <br />
                <Heading
                  level="3"
                  className="text-sm font-semibold text-gray-900"
                >
                  After you click the button below
                </Heading>
                <ul className="ml-4 text-base">
                  <li>
                    1. Lumeo will redirect you to the Ring app to re-confirm the
                    integration
                  </li>
                  <li>
                    2. After you re-confirm the integration in the Ring app,
                    Lumeo will import the authorized Ring cameras into your
                    Lumeo account
                  </li>
                </ul>
                <FormMessage intent="warning" icon="info">
                  After re-confirming in Ring app, we recommend you return to
                  Lumeo console using <b>a desktop browser</b> to finish setting
                  up the AI workflows. <br />
                  After setup, you can use Lumeo mobile apps to view motion
                  event recordings, dashboards, and alerts.
                </FormMessage>
              </div>
            </>
          )}

          {ringLinkState === 'in_progress' && (
            <>
              <Text size="large" className="text-center text-gray-600">
                Ring requires you to re-confirm the integration. Please check
                the Ring app to complete the setup.
              </Text>

              <div className="mt-8 space-y-4 rounded-lg border border-gray-400 p-6">
                <Heading
                  level="3"
                  className="text-sm font-semibold text-gray-900"
                >
                  Need to restart?
                </Heading>
                <Text size="small" className="text-gray-600">
                  If something went wrong during the linking process, you can
                  reset the integration. This will:
                </Text>
                <ul className="ml-4 list-disc text-sm text-gray-600">
                  <li>Reset the linking state</li>
                  <li>Log you out of Lumeo</li>
                  <li>
                    Require you to restart the process from the Ring app and
                    login to your new account again
                  </li>
                </ul>
                <div className="flex justify-center pt-2">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleResetLinking}
                    loading={isResetting}
                  >
                    Reset Linking
                  </Button>
                </div>
              </div>
            </>
          )}
        </header>

        {ringLinkState === 'unlinked' && (
          <div className="flex justify-center pt-4">
            <Button
              variant="primary"
              size="large"
              onClick={handleConfirmClick}
              loading={isConfirming}
            >
              Confirm and go to Ring
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
